import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaThumbsUp, 
  FaFlag, 
  FaUser, 
  FaUserSecret, 
  FaCheckCircle,
  FaCalendarAlt
} from 'react-icons/fa';

import StarRating from './StarRating';
import { markRatingHelpful, reportRating } from '../../utils/restaurantApi';
import { formatRatingDate } from '../../utils/ratingHelpers';
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../../components/NotificationManager';

/**
 * Individual Rating Display Component
 * Shows a single rating with user interaction options
 */
const RatingDisplay = ({
  rating,
  showActions = true,
  showCriteria = true,
  className = ''
}) => {
  const [isHelpful, setIsHelpful] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(rating.helpfulVotes || 0);
  const [isReporting, setIsReporting] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);

  // Handle helpful vote
  const handleHelpful = async () => {
    if (isHelpful) return; // Already marked as helpful

    try {
      await markRatingHelpful(rating._id);
      setIsHelpful(true);
      setHelpfulCount(prev => prev + 1);
      showSuccessNotification('Thank you for marking this review as helpful!');
    } catch (error) {
      showErrorNotification(error.message || 'Failed to mark as helpful');
    }
  };

  // Handle report
  const handleReport = async (reason) => {
    try {
      setIsReporting(true);
      await reportRating(rating._id, reason);
      setShowReportForm(false);
      showSuccessNotification('Thank you for reporting. We will review this content.');
    } catch (error) {
      showErrorNotification(error.message || 'Failed to report rating');
    } finally {
      setIsReporting(false);
    }
  };

  const criteriaLabels = {
    overall: 'Overall',
    foodQuality: 'Food Quality',
    deliverySpeed: 'Delivery Speed',
    customerService: 'Customer Service',
    valueForMoney: 'Value for Money',
    packaging: 'Packaging'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow ${className}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center">
            {rating.isAnonymous ? (
              <FaUserSecret className="text-orange-600" />
            ) : (
              <FaUser className="text-orange-600" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-800">
                {rating.isAnonymous ? 'Anonymous' : rating.user?.username || 'User'}
              </h4>
              {rating.isVerifiedPurchase && (
                <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                  <FaCheckCircle className="text-xs" />
                  Verified Purchase
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <FaCalendarAlt className="text-xs" />
              {formatRatingDate(rating.createdAt)}
            </div>
          </div>
        </div>
        
        {/* Overall Rating */}
        <div className="text-right">
          <StarRating 
            value={rating.ratings?.overall || 0} 
            readonly 
            size="sm" 
            showValue 
          />
        </div>
      </div>

      {/* Criteria Ratings */}
      {showCriteria && rating.ratings && (
        <div className="mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(rating.ratings)
              .filter(([key, value]) => key !== 'overall' && value > 0)
              .map(([criteria, value]) => (
                <div key={criteria} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{criteriaLabels[criteria]}:</span>
                  <StarRating value={value} readonly size="sm" />
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Review Text */}
      {rating.reviewText && (
        <div className="mb-4">
          <p className="text-gray-700 leading-relaxed">
            "{rating.reviewText}"
          </p>
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4">
            {/* Helpful Button */}
            <button
              onClick={handleHelpful}
              disabled={isHelpful}
              className={`flex items-center gap-2 text-sm transition-colors ${
                isHelpful 
                  ? 'text-green-600 cursor-default' 
                  : 'text-gray-500 hover:text-green-600'
              }`}
            >
              <FaThumbsUp className={isHelpful ? 'text-green-600' : ''} />
              <span>Helpful ({helpfulCount})</span>
            </button>

            {/* Report Button */}
            <button
              onClick={() => setShowReportForm(true)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              <FaFlag />
              Report
            </button>
          </div>
        </div>
      )}

      {/* Report Form Modal */}
      {showReportForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowReportForm(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4">Report Review</h3>
            <p className="text-gray-600 mb-4">
              Why are you reporting this review?
            </p>
            
            <div className="space-y-2 mb-6">
              {[
                { value: 'inappropriate', label: 'Inappropriate content' },
                { value: 'spam', label: 'Spam or fake review' },
                { value: 'offensive', label: 'Offensive language' },
                { value: 'other', label: 'Other reason' }
              ].map((reason) => (
                <button
                  key={reason.value}
                  onClick={() => handleReport(reason.value)}
                  disabled={isReporting}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {reason.label}
                </button>
              ))}
            </div>
            
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowReportForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default RatingDisplay;